import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { TreeNode, TreeViewMode } from '../../types';
import { TreePersonNode } from './TreePersonNode';

/**
 * SimpleTreeView Component
 *
 * Renders family tree as traditional hierarchical collapsible tree
 *
 * Layout:
 * - Traditional parent→child hierarchy (descendant mode)
 * - Or child→parent hierarchy (ancestor mode)
 * - Indentation shows depth
 * - Expand/collapse to show/hide children/parents
 * - Clean, simple vertical layout
 *
 * @component
 */
interface SimpleTreeViewProps {
  /** Root node of the tree */
  root: TreeNode;

  /** Callback when person node is clicked */
  onPersonClick: (personId: string) => void;

  /** View mode - determines if we show children or parents */
  viewMode?: TreeViewMode;

  /** External control to expand all nodes */
  expandAll?: boolean;

  /** External control to collapse all nodes */
  collapseAll?: boolean;

  /** Callback when expand/collapse state changes */
  onExpandCollapseComplete?: () => void;
}

export const SimpleTreeView: React.FC<SimpleTreeViewProps> = ({
  root,
  onPersonClick,
  viewMode = 'descendant',
  expandAll,
  collapseAll,
  onExpandCollapseComplete
}) => {
  // Track expanded state for each node
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Auto-expand first 2 levels by default
    const initialExpanded = new Set<string>();
    const autoExpand = (node: TreeNode, currentLevel: number) => {
      if (currentLevel < 2) {
        initialExpanded.add(node.person.id);

        // Expand children (descendant mode)
        node.children?.forEach(child => autoExpand(child, currentLevel + 1));

        // Expand parents (ancestor mode)
        if (node.parents) {
          if (node.parents.father) autoExpand(node.parents.father, currentLevel + 1);
          if (node.parents.mother) autoExpand(node.parents.mother, currentLevel + 1);
        }
      }
    };
    autoExpand(root, 0);
    return initialExpanded;
  });

  // ===== Effects =====

  /**
   * Handle expand all / collapse all
   */
  useEffect(() => {
    if (expandAll) {
      // Collect all person IDs in the tree
      const allIds = new Set<string>();
      const collectIds = (node: TreeNode) => {
        allIds.add(node.person.id);

        // Collect from children (descendant mode)
        node.children?.forEach(collectIds);

        // Collect from parents (ancestor mode)
        if (node.parents) {
          if (node.parents.father) collectIds(node.parents.father);
          if (node.parents.mother) collectIds(node.parents.mother);
        }
      };
      collectIds(root);
      setExpandedNodes(allIds);
      onExpandCollapseComplete?.();
    } else if (collapseAll) {
      // Collapse all nodes
      setExpandedNodes(new Set());
      onExpandCollapseComplete?.();
    }
  }, [expandAll, collapseAll, root, onExpandCollapseComplete]);

  // ===== Event Handlers =====

  /**
   * Toggle expand/collapse for a node
   */
  const handleToggleExpand = (personId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(personId)) {
        newSet.delete(personId);
      } else {
        newSet.add(personId);
      }
      return newSet;
    });
  };

  /**
   * Check if node is expanded
   */
  const isNodeExpanded = (personId: string): boolean => {
    return expandedNodes.has(personId);
  };

  // ===== Render Functions =====

  /**
   * Recursively render a node and its children/parents
   */
  const renderNode = (node: TreeNode, depth: number = 0): React.ReactNode => {
    const isExpanded = isNodeExpanded(node.person.id);
    const indentPx = depth * 40; // 40px per level

    // Determine what to expand based on view mode
    const hasExpandable = viewMode === 'descendant'
      ? (node.children && node.children.length > 0)
      : (node.parents && (node.parents.father || node.parents.mother));

    return (
      <Box key={node.person.id}>
        {/* Current Node */}
        <Box
          sx={{
            ml: `${indentPx}px`,
            mb: 1.5,
            position: 'relative',
          }}
        >
          <TreePersonNode
            node={{
              ...node,
              isExpanded,
            }}
            onPersonClick={onPersonClick}
            onToggleExpand={hasExpandable ? () => handleToggleExpand(node.person.id) : undefined}
          />
        </Box>

        {/* Descendant Mode: Render Children */}
        {viewMode === 'descendant' && node.children && node.children.length > 0 && isExpanded && (
          <Box sx={{ position: 'relative' }}>
            {/* Vertical connecting line for children */}
            {depth >= 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${indentPx + 20}px`,
                  top: 0,
                  bottom: 24,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />
            )}

            {/* Render each child */}
            {node.children.map((child) => (
              <Box key={child.person.id} sx={{ position: 'relative' }}>
                {/* Horizontal connecting line to child */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${indentPx + 20}px`,
                    top: 24,
                    width: 20,
                    height: 2,
                    bgcolor: 'divider',
                  }}
                />
                {renderNode(child, depth + 1)}
              </Box>
            ))}
          </Box>
        )}

        {/* Ancestor Mode: Render Parents */}
        {viewMode === 'ancestor' && node.parents && (node.parents.father || node.parents.mother) && isExpanded && (
          <Box sx={{ position: 'relative' }}>
            {/* Vertical connecting line for parents */}
            {depth >= 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${indentPx + 20}px`,
                  top: 0,
                  bottom: 24,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />
            )}

            {/* Render father */}
            {node.parents.father && (
              <Box key={node.parents.father.person.id} sx={{ position: 'relative' }}>
                {/* Horizontal connecting line to parent */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${indentPx + 20}px`,
                    top: 24,
                    width: 20,
                    height: 2,
                    bgcolor: 'divider',
                  }}
                />
                {renderNode(node.parents.father, depth + 1)}
              </Box>
            )}

            {/* Render mother */}
            {node.parents.mother && (
              <Box key={node.parents.mother.person.id} sx={{ position: 'relative' }}>
                {/* Horizontal connecting line to parent */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${indentPx + 20}px`,
                    top: 24,
                    width: 20,
                    height: 2,
                    bgcolor: 'divider',
                  }}
                />
                {renderNode(node.parents.mother, depth + 1)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // ===== Render =====

  return (
    <Box
      sx={{
        width: '100%',
        py: 3,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {renderNode(root)}
    </Box>
  );
};
